import { NgClass } from '@angular/common';
import { Component, computed, DestroyRef, inject, OnInit, Signal, signal, TemplateRef, ViewChild } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormsModule } from '@angular/forms';
import { Router, RouterLink } from '@angular/router';
import { PermissionDirective } from '@app/core/directives/permission.directive';
import { Events } from '@app/core/models/event-map';
import { AuthApiService } from '@app/core/services/auth-api.service';
import { PublishSubscribeService } from '@app/core/services/publish-subscribe.service';
import { CheckBoxComponent } from '@app/shared/components/check-box/check-box.component';
import { ContactSupportComponent } from '@app/shared/components/modals/contact-support/contact-support.component';
import { EstimatedDeliveryWeeksComponent } from '@app/shared/components/modals/estimated-delivery-weeks/estimated-delivery-weeks.component';
import { UserSetupComponent } from '@app/shared/components/settings/user-setup/user-setup.component';
import { PortalPermissionRole } from '@app/shared/models/portal-permission-role.model';
import { PortalUser } from '@app/shared/models/portal-user.model';
import { PortalApiService } from '@app/shared/services/portal-api.service';
import { UserService } from '@app/shared/services/User.Service';
import { SetSelectedUser, SetShowMeasurements, SetShowPricesValue, SetShowUValue, SetShowVat } from '@app/shared/state/user-context/user-context.action';
import { UserContextSelectors } from '@app/shared/state/user-context/user-context.selectors';
import { Store } from '@ngxs/store';
import { orderBy } from 'lodash-es';
import { BsModalService } from 'ngx-bootstrap/modal';
import { finalize, Subscription } from 'rxjs';
import { InitialsComponent } from '../initials/initials.component';

@Component({
    selector: 'top-nav',
    imports: [
        UserSetupComponent,
        PermissionDirective,
        FormsModule,
        NgClass,
        InitialsComponent,
        EstimatedDeliveryWeeksComponent,
        ContactSupportComponent,
        RouterLink,
        CheckBoxComponent
    ],
    templateUrl: './top-nav.component.html',
    styleUrl: './top-nav.component.scss'
})
export class TopNavComponent implements OnInit {

    @ViewChild('editUserProfileTemplate', { static: false }) private editUserProfileTemplate!: TemplateRef<void>;
    readonly userProfileModalId: string = 'userProfileModalId'

    @ViewChild('contactSupportTemplate', { static: false }) private contactSupportTemplate!: TemplateRef<void>;
    readonly contactSupportModalId: string = 'contactSupportModalId'

    @ViewChild('estimatedDeliveryWeeksTemplate', { static: false }) private estimatedDeliveryWeeksTemplate!: TemplateRef<void>;
    readonly estimatedDeliveryWeeksModalId: string = 'estimatedDeliveryWeeksModalId'

    #destroyRef = inject(DestroyRef)
    #subscriptions: Subscription[] = []
    #publishSubscribeService = inject(PublishSubscribeService)
    #router = inject(Router)
    #authService = inject(AuthApiService)
    #store = inject(Store)
    #userInternalService = inject(UserService)
    #modalService = inject(BsModalService)
    #portalApiService = inject(PortalApiService)

    showPrices = this.#store.selectSignal(UserContextSelectors.showPricesValue)
    showWithVAT = this.#store.selectSignal(UserContextSelectors.showVat)
    showUValue = this.#store.selectSignal(UserContextSelectors.showUValue)
    showMeasurements = this.#store.selectSignal(UserContextSelectors.showMeasurements)
    isAftermarket = this.#store.selectSignal(UserContextSelectors.isAftermarket)
    isAssembler = this.#store.selectSignal(UserContextSelectors.isAssembler)
    isAdmin = this.#store.selectSignal(UserContextSelectors.isAdministrator)
    userFullName = this.#store.selectSignal(UserContextSelectors.userFullname)
    newRole = signal<string>('')
    isCustAdmin = this.#store.selectSignal(UserContextSelectors.isCustomerAdministrator)
    isMF = this.#store.selectSignal(UserContextSelectors.isMf)
    isReseller = this.#store.selectSignal(UserContextSelectors.isReseller)

    portalUsers = signal<PortalUser[]>([])
    portalPermissionRoles = signal<PortalPermissionRole[]>([])
    loadingRoleChange = signal<boolean>(false)
    isSubmenuVisible = signal<boolean>(false)
    isPortalPermissionRolesVisible = signal<boolean>(false)

    allowRoleChange: Signal<boolean> = computed(() => {
        return this.isCustAdmin() && this.isMF()
    })

    ngOnInit(): void {
        // As we propably already is logged in, setData
        this.setData()
        this.registerEventSubscriptions()
        this.setShowVat(this.isMF())
    }

    private registerEventSubscriptions(): void {
        this.#subscriptions.push(
            this.#publishSubscribeService.on(Events.UserService.PORTAL_USERS_CHANGED).pipe(
                takeUntilDestroyed(this.#destroyRef)
            ).subscribe({
                next: () => {
                    this.handleEvent_UserService_PORTAL_USERS_CHANGED()
                }
            })
        )

        this.#subscriptions.push(
            this.#publishSubscribeService.on(Events.UserService.USER_LOGGED_IN).pipe(
                takeUntilDestroyed(this.#destroyRef)
            ).subscribe({
                next: () => {
                    this.handleEvent_UserService_PORTAL_USERS_CHANGED()
                }
            })
        )

        this.#subscriptions.push(
            this.#publishSubscribeService.on(Events.UserService.SET_PORTAL_PERMISSION_ROLE).pipe(
                takeUntilDestroyed(this.#destroyRef)
            ).subscribe({
                next: (event: { role: string }) => {
                    this.handleEvent_UserService_SET_PORTAL_PERMISSION_ROLE(event)
                }
            })
        )
    }

    private getPortalPermissionRoles() {

        if (this.portalPermissionRoles().length > 0) { return }

        this.#portalApiService.getPortalPermissionRole()
            .subscribe({
                next: (response) => {
                    if (response) {
                        this.portalPermissionRoles.set(response)
                    }
                },
                error: (err) => {
                    console.log("Fetch_PortalPermissionRole", JSON.stringify(err));
                }
            })
    }

    openModalContactSupport(): void {
        this.#modalService.show(this.contactSupportTemplate, { id: this.contactSupportModalId, class: 'modal-md', backdrop: 'static' })
    }

    openModalEditUserDialog(): void {
        this.#modalService.show(this.editUserProfileTemplate, { id: this.userProfileModalId, class: 'modal-lg', backdrop: 'static' })
    }

    openModalEstimatedDlvWeeks(): void {
        this.#modalService.show(this.estimatedDeliveryWeeksTemplate, { id: this.estimatedDeliveryWeeksModalId, class: 'modal-lg', backdrop: 'static' })
    }

    routeTo(view: string | undefined = ''): void {
        this.#router.navigateByUrl(`/${view}`);
    }

    setSelectedPortalUser(portalUser: PortalUser): void {
        this.#store.dispatch(new SetSelectedUser(portalUser.NETWORKALIAS, portalUser.NETWORKDOMAIN))
        this.#userInternalService.setSelectedPortalUser(portalUser)
    }

    setShowPrices(checked: boolean) {
        this.#store.dispatch(new SetShowPricesValue(checked))
        this.#userInternalService.setShowPrices(checked)
    }

    setShowVat(checked: boolean) {
        this.#store.dispatch(new SetShowVat(checked))
    }

    setShowUValue(checked: boolean) {
        this.#store.dispatch(new SetShowUValue(checked))
    }

    setShowMeasurements(checked: boolean) {
        this.#store.dispatch(new SetShowMeasurements(checked))
    }

    setPortalPermissionRoles(newRole: string): void {

        this.newRole.set(newRole)

        this.loadingRoleChange.set(true)

        this.#portalApiService.setPortalPermissionRole(newRole)
            .pipe(finalize(() => this.loadingRoleChange.set(false)))
            .subscribe({
                next: (response) => {
                    if (response && response?.Message) {
                        this.#publishSubscribeService.trigger(Events.UserService.SET_PORTAL_PERMISSION_ROLE, { role: response.Message });
                    }
                },
                error: (err) => { console.error('setPortalPermissionRole', err?.message ?? err?.error) }
            })

        this.isPortalPermissionRolesVisible.update( v => v = !v)
    }

    toggleSubMenu(event: MouseEvent): void {
        event.stopPropagation()
        event.preventDefault()

        this.isSubmenuVisible.update( v => v = !v)
    }

    togglePortalPermissionRoles(event: MouseEvent): void {
        event.stopPropagation()
        event.preventDefault()

        this.isPortalPermissionRolesVisible.update( v => v = !v)
    }

    handleEvent_UserService_PORTAL_USERS_CHANGED(): void {
        this.setData();
    }

    handleEvent_UserService_SET_PORTAL_PERMISSION_ROLE(event: { role: string }): void {
        if (event?.role) {
            this.newRole.set(event.role)
        }
    }

    setData(): void {
      if (this.#store.selectSnapshot(UserContextSelectors.isAuthenticated)) {
          this.portalUsers.set(orderBy(this.#userInternalService.getPortalUsers().filter(u => u?.USERFULLNAME), ['USERFULLNAME']))
          this.getPortalPermissionRoles()
      }
    }

    logout(): void {
        this.#authService.logout(undefined, false)
    }
}
